import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Tooltips de la sidebar COLAPSADA del shell nuevo.
 *
 * Colapsada, cada ítem es sólo un ícono: el nombre lo daba el `title` nativo, que
 * tarda lo que el sistema operativo quiera (1,5–2s en la mayoría), sale pegado al
 * cursor —no al ítem— y se dibuja con el chrome del navegador, que no se parece en
 * nada al resto de la app.
 *
 * Acá va uno propio: aparece **al segundo** de entrar al ítem, anclado al centro
 * vertical del ícono y a su derecha, con el mismo lenguaje visual del shell.
 *
 * **Va por portal a `document.body`, no adentro del `<aside>`.** La sidebar es
 * `overflow-hidden` (lo necesita para el fade de ancho al colapsar), así que un
 * tooltip absolute adentro se recortaría en el borde. Por eso las coordenadas se
 * miden con `getBoundingClientRect()` al momento de mostrarlo y la caja va `fixed`.
 */

/** Lo que tarda en aparecer desde que el cursor entra al ítem. */
const DEMORA_MS = 500;

/**
 * Ventana en la que el siguiente tooltip sale SIN esperar.
 *
 * Recorrer la sidebar leyendo ítem por ítem es el caso de uso: si cada uno cobrara
 * su segundo entero, el recorrido se vuelve una espera por ícono. Una vez que el
 * usuario "entró en modo leer etiquetas", los vecinos responden al toque.
 */
const GRACIA_MS = 400;

interface Tip {
  label: string;
  /** Borde izquierdo del tooltip: el canto derecho del ítem + aire. */
  x: number;
  /** Centro vertical del ítem (la caja se centra sola con el translate). */
  y: number;
}

/** Handlers para pegarle a un disparador con `{...propsTooltip('Etiqueta')}`. */
type PropsDisparador = Record<string, unknown>;

export function useTooltipsSidebar(activo: boolean) {
  const [tip, setTip] = useState<Tip | null>(null);
  const tipRef = useRef<Tip | null>(null);
  const temporizador = useRef<number | null>(null);
  const ultimoCierre = useRef(0);

  /**
   * Sólo con puntero fino y hover real. En touch el `mouseenter` sintético del tap
   * dispararía un tooltip que nadie pidió, encima del contenido al que se acaba de
   * navegar.
   */
  const conHover =
    typeof window !== 'undefined' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const habilitado = activo && conHover;

  const limpiar = useCallback(() => {
    if (temporizador.current !== null) {
      clearTimeout(temporizador.current);
      temporizador.current = null;
    }
  }, []);

  const ocultar = useCallback(() => {
    limpiar();
    if (tipRef.current) {
      ultimoCierre.current = Date.now();
      tipRef.current = null;
      setTip(null);
    }
  }, [limpiar]);

  const mostrar = useCallback((label: string, el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    const nuevo = { label, x: r.right + 10, y: r.top + r.height / 2 };
    tipRef.current = nuevo;
    setTip(nuevo);
  }, []);

  // Al expandir la sidebar (o al salir de la pantalla) no queda ningún tooltip
  // colgado: el ítem que lo abrió ahora muestra su etiqueta impresa.
  useEffect(() => {
    if (!habilitado) ocultar();
  }, [habilitado, ocultar]);

  // Las coordenadas son de un instante. Si algo se mueve debajo —scroll de la lista
  // de herramientas, resize de la ventana— la caja quedaría apuntando al vacío, así
  // que se va. `capture` para enterarse también del scroll de contenedores internos,
  // que no burbujea.
  useEffect(() => {
    if (!tip) return;
    const alMover = () => ocultar();
    window.addEventListener('scroll', alMover, true);
    window.addEventListener('resize', alMover);
    return () => {
      window.removeEventListener('scroll', alMover, true);
      window.removeEventListener('resize', alMover);
    };
  }, [tip, ocultar]);

  useEffect(() => () => limpiar(), [limpiar]);

  /**
   * @param label     lo que dice el tooltip.
   * @param conAria   además lo deja como nombre accesible del control. Va en `false`
   *                  cuando el disparador es un `<span>` decorativo adentro de un
   *                  botón que ya tiene el suyo: un `aria-label` sobre un elemento
   *                  sin rol no se anuncia y encima compite con el del botón.
   */
  const propsTooltip = useCallback(
    (label: string, { conAria = true }: { conAria?: boolean } = {}): PropsDisparador => {
      if (!habilitado) return {};
      return {
        onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
          const el = e.currentTarget;
          limpiar();
          if (Date.now() - ultimoCierre.current < GRACIA_MS) mostrar(label, el);
          else temporizador.current = window.setTimeout(() => mostrar(label, el), DEMORA_MS);
        },
        onMouseLeave: ocultar,
        // Al clickear el ítem el tooltip sobra: ya se está yendo a la pantalla que
        // nombra.
        onMouseDown: ocultar,
        // Con teclado sale al toque y sin demora: ahí el tooltip no es una ayuda
        // opcional, es la única forma de saber en qué ítem está parado el foco.
        // `:focus-visible` deja afuera el foco que da el click, que ya se manejó.
        onFocus: (e: React.FocusEvent<HTMLElement>) => {
          if (e.currentTarget.matches(':focus-visible')) mostrar(label, e.currentTarget);
        },
        onBlur: ocultar,
        ...(conAria ? { 'aria-label': label } : {}),
      };
    },
    [habilitado, limpiar, mostrar, ocultar],
  );

  const tooltip =
    tip &&
    createPortal(
      <div
        role="tooltip"
        style={{ position: 'fixed', left: tip.x, top: tip.y }}
        // z-500: por ENCIMA de todo lo que convive con la sidebar. El panel de las
        // pantallas de modo es `fixed` con `z-100` y tapaba el globo entero —se veía
        // sólo la puntita, del lado de la sidebar—; los sheets de la app llegan a 211.
        // Sigue MUY por debajo de los modales de 9998+, que igual no importan: mientras
        // uno está abierto su backdrop se come el hover y el tooltip no se dispara.
        className="animate-tooltip-lateral pointer-events-none z-[500] -translate-y-1/2"
      >
        <div className="relative rounded-lg bg-[#16181D] px-2.5 py-1.5 text-[12.5px] font-[500] leading-[16px] text-white shadow-[0_10px_28px_-8px_rgba(0,0,0,0.45)]">
          {/* Puntita: un cuadrado rotado del mismo color, medio metido debajo de la
              caja para que no se le vea el canto. */}
          <span className="absolute -left-[3px] top-1/2 h-[7px] w-[7px] -translate-y-1/2 rotate-45 rounded-[1px] bg-[#16181D]" />
          <span className="relative whitespace-nowrap">{tip.label}</span>
        </div>
      </div>,
      document.body,
    );

  return { propsTooltip, tooltip };
}

export default useTooltipsSidebar;
